import { useEffect, useMemo } from "react";

const PricingPlanInfoModal = ({
  open,
  onClose,
  sections,
  title = "Scholar Admission Program",
  subtitle = "Explore your pathway to global education : Guiding you from school to global success",
}) => {
  const defaultSections = useMemo(
    () => [
      {
        title: "Scholar Undergraduate Admission Program (SUAP)",
        subtitle:
          "A guided admission program for school students aspiring for global undergraduate degrees.",
        badge: "Bachelors",
        color: "#572575",
        backgroundColor: "#FBFAFC",
        accentColor: "#C8A5DF",
        items: [
          {
            title: "Accelerated SUAP (Class 8–12)",
            description:
              "Early start for school students with guidance on profile building, course & university shortlisting, and visa & offer letter support.",
          },
          {
            title: "Germany UG Premium",
            description:
              "Early start for school students with guidance on profile building, course & university shortlisting, and visa & offer letter support.",
          },
          {
            title: "UG Premium (6/10 Universities)",
            description:
              "Focused program for select universities with simplified admission assistance.",
          },
          {
            title: "Psychometric Assessment",
            description:
              "Discover strengths, interests, and career direction to choose the right academic path.",
          },
        ],
      },
      {
        title: "Scholar Postgraduate Admission Program (SPAP)",
        subtitle:
          "Advanced admission program for graduates aiming at global master’s and MBA programs.",
        badge: "Masters",
        color: "#572575",
        backgroundColor: "#FBFAFC",
        accentColor: "#C8A5DF",
        items: [
          {
            title: "MS / MBA / Premium Programs",
            description:
              "Comprehensive postgraduate guidance including course & university shortlisting, and visa & offer letter support.",
          },
          {
            title: "Germany PG Premium",
            description:
              "Tailored program for postgraduate admissions in German universities.",
          },
          {
            title: "PG Premium (Except Germany)",
            description:
              "Guidance for top universities worldwide with complete admission support.",
          },
          {
            title: "MBA / MS Tracks",
            description:
              "Specialized counseling for business and technical programs with complete visa & offer letter assistance.",
          },
        ],
      },
    ],
    []
  );

  const data =
    Array.isArray(sections) && sections.length ? sections : defaultSections;

  // Close modal on ESC key
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-3 md:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative bg-white w-full max-w-3xl rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-6 text-center text-white bg-[#461E5E]">
          <div
            className="font-siromi text-[30px] leading-[100%] tracking-[0px] text-center"
            style={{ fontWeight: 400 }}
          >
            {title}
          </div>

          <p
            className="font-altisunisa text-[17px] leading-[100%] tracking-[0px] text-center text-[#EFF1F5] mt-4"
            style={{ fontWeight: 200 }}
          >
            {subtitle}
          </p>

          {/* Close Button */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-full bg-[#E53935]"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="max-h-[75vh] overflow-y-auto px-4 md:px-6 pb-6 pt-6 space-y-6 scrollbar-hide">
          {data.map((section, index) => (
            <div
              key={index}
              className="rounded-2xl border p-4 md:p-6"
              style={{
                backgroundColor: section.backgroundColor,
                borderColor: `${section.color}50`,
              }}
            >
              {/* Section Header */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-altisunisa font-bold text-[20px] text-[#161719]">
                    {section.title}
                  </h3>
                  <p className="font-altisunisa font-normal text-sm md:text-base text-gray-600 mt-1">
                    {section.subtitle}
                  </p>
                </div>

                {/* Ribbon badge with left & right cuts */}
                <div className="relative inline-flex items-center select-none">
                  {/* Left cut */}
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2
               w-0 h-0 border-t-[10px] border-t-transparent
               border-b-[10px] border-b-transparent
               border-l-[10px] border-l-white"
                  ></span>  
                            
                  {/* Ribbon body */}
                  <span
                    className="px-6 py-1.5 text-sm md:text-base font-semibold text-white text-center"
                    style={{ backgroundColor: section.color }}
                  >
                    {section.badge}
                  </span>

                  {/* Right cut */}
                  <span
                    className={`absolute right-0 top-1/2 -translate-y-1/2
               w-0 h-0 border-t-[10px] border-t-transparent
               border-b-[10px] border-b-transparent
               border-r-[10px] border-r-white`}
                  ></span>
                </div>
              </div>

              {/* Dashed divider */}
              <hr className={`my-4 border-dashed border-${section.color}`} />

              {/* Program Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {section.items.map((item, i) => (
                  <div
                    key={i}
                    className="relative mx-6 rounded-xl border border-gray-200 bg-white z-[1100] shadow-sm p-2 md:p-5"
                  >
                    {/* Accent bar */}
                    <div
                      className="absolute -top-2 left-0 right-4 h-3 rounded-t-md"
                      style={{ backgroundColor: section.accentColor }}
                    />
                    {/* Left Accent bar */}
                    <div
                      className="absolute top-0 bottom-1 left-0 h-full w-2 rounded-l-xl"
                      style={{ backgroundColor: section.accentColor }}
                    />

                    <h4 className="font-altisunisa font-normal text-[16px] leading-[100%] tracking-normal text-[#161719]">
                      {item.title}
                    </h4>

                    <p className="mt-2 text-[8px] md:text-[13px] font-altisunisa font-normal leading-[100%] tracking-normal text-[#3F424C]">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingPlanInfoModal;
